<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { location, push, replace } from 'svelte-spa-router';
	//Helpers
	import {
		addDashboard,
		deleteDashboard,
		checkIsEventLogTab,
		requestChangeDashboardName,
	} from '../list-helpers';
	import { getBackgroundColorPreviewImages } from 'src/helpers/background_color_helpers';
	// Stores
	import { dashboards, dashboardTabs } from 'src/stores/features/dashboards';
	import { user_data, user_settings } from 'src/stores';
	import { NON_PREMIUM_DASH_TABS_LIMIT, PREMIUM_DASH_TABS_LIMIT } from 'src/constants';
	// Components
	import {
		Button,
		Modal,
		Text,
		Container,
		Input,
		Icon,
		Alert,
		SelectCard,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
	} from '@Shelly/shelly-design-system';
	import ImageList from '../ImageListV2.svelte';
	// Types
	import type { TypeTab } from '../../../types/Tabs';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		createNewDashWithDefaultWidgets,
		DEFAULT_DASH_ID,
		mapDashToAvatar,
	} from 'src/components/Dashboards/dash-helpers';
	import { createAsyncAction } from 'src/helpers/async_action_guard';
	import type { DashboardKind } from 'src/components/Dashboards/dash-types';
	import { initializeCustomCameraDashboard } from 'src/components/Camera/helpers/camera-monitoring-settings';
	import {
		ENERGY_V2_PRESETS,
		ENERGY_V2_PURPOSE_TEMPLATES,
		type EnergyPurposeId,
	} from 'src/components/EnergyMonitoring/v2/energy-v2-types';
	import { initializeCustomEnergyDashboard } from 'src/components/EnergyMonitoring/v2/energy-v2-dashboard';
	import { getEnergyWidgetDefinition } from 'src/components/Dashboards/dash-energy-registry';

	const dispatch = createEventDispatcher();
	const isPremium = $user_data.premium || $user_settings.been_premium;
	const DASHBOARDS_LIMIT = isPremium ? PREMIUM_DASH_TABS_LIMIT : NON_PREMIUM_DASH_TABS_LIMIT;
	const backgroundColorPreviewImages = getBackgroundColorPreviewImages();
	const DEFAULT_DASH_ICON = 'fat fa-objects-column';

	const DASHBOARD_KIND_OPTIONS = [
		{
			id: 'standard' as DashboardKind,
			icon: 'fas fa-grid-2',
			labelKey: 'option_custom',
			labelDefault: 'Custom',
			descriptionKey: 'dashboard_type_custom_description',
			descriptionDefault: 'An empty dashboard you can shape widget by widget.',
		},
		{
			id: 'energy' as DashboardKind,
			icon: 'fas fa-bolt',
			labelKey: 'label_energy',
			labelDefault: 'Energy',
			descriptionKey: 'dashboard_type_energy_description',
			descriptionDefault: 'Purpose-built views for energy, PV, rooms, and devices.',
		},
		{
			id: 'camera' as DashboardKind,
			icon: 'fas fa-video',
			labelKey: 'camera',
			labelDefault: 'Camera',
			descriptionKey: 'dashboard_type_camera_description',
			descriptionDefault: 'A monitoring layout optimized for camera feeds.',
		},
	] as const;

	$: dashboardKindOptions = DASHBOARD_KIND_OPTIONS.map((option) => ({
		id: option.id,
		icon: option.icon,
		label: $format(option.labelKey, { default: option.labelDefault }),
		description: $format(option.descriptionKey, {
			default: option.descriptionDefault,
		}),
	}));

	const alertMessegaLimitTranslation = isPremium
		? 'alert_premium_dashboard_limit_reached'
		: 'alert_basic_dashboard_limit_reached';

	const alertMessageReachedLimitDefault = isPremium
		? `Dashboard limit of ${PREMIUM_DASH_TABS_LIMIT} reached! Remove some of your dashboards to add a new one.`
		: `Dashboard limit of ${NON_PREMIUM_DASH_TABS_LIMIT} reached! Upgrade to Premium to create up to ${PREMIUM_DASH_TABS_LIMIT} dashboards.`;

	const plan = isPremium ? $format('label_premium') : $format('label_basic_plan');

	let dashToDelete = null;
	let editDashId: string | null = null;
	let dashboardKind: DashboardKind = 'standard';
	let energyPurpose: EnergyPurposeId = 'home';
	let wizardStep = 0;
	const { loading: deleteLoading, run: runDelete } = createAsyncAction();
	const { loading: isSaving, run: runSave } = createAsyncAction();

	export let dashboardsEdit = false;
	export let editDashObj: any = {
		name: '',
		icon: DEFAULT_DASH_ICON,
		backgroundColor: '',
	};
	export let showDeleteModal = false;
	export let isEditDash = false;

	$: if (isEditDash || showDeleteModal) {
		const parts = $location.split('/');
		editDashId = parts[parts.length - 1];

		const dashToEdit = $dashboards[editDashId];

		if (dashToEdit) {
			if (showDeleteModal) {
				dashToDelete = dashToEdit;
			}

			// Same fallback as the tabs rail: missing icon → default column icon.
			const avatar = mapDashToAvatar(dashToEdit);
			editDashObj = {
				name: dashToEdit.name,
				icon: dashToEdit.icon?.trim() || avatar.icon || DEFAULT_DASH_ICON,
				backgroundColor: dashToEdit.backgroundColor?.trim() || '',
			};
		}
	}

	$: disableAdd = !editDashObj?.icon?.trim() || !editDashObj?.name?.trim();

	interface DashboardTab extends TypeTab {
		isDndShadowItem?: boolean;
	}

	let items: DashboardTab[];
	$: items = [...$dashboardTabs].filter((tab) => !checkIsEventLogTab(tab));

	$: listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});

	const ICONS_DB = [
		{ id: 0, icon: DEFAULT_DASH_ICON, type: 'icon', isIcon: true },
		{ id: 1, icon: 'fat fa-microchip', type: 'icon', isIcon: true },
		{ id: 2, icon: 'fat fa-bolt', type: 'icon', isIcon: true },
		{ id: 3, icon: 'fat fa-garage', type: 'icon', isIcon: true },
		{ id: 4, icon: 'fat fa-server', type: 'icon', isIcon: true },
		{ id: 5, icon: 'fat fa-oven', type: 'icon', isIcon: true },
	];

	// Keep the picker highlight in sync with the active icon (defaults to the
	// same column icon the tabs rail uses when a dashboard has no icon set).
	$: icons = ICONS_DB.map((item) => ({
		...item,
		selected: item.icon === (editDashObj?.icon || DEFAULT_DASH_ICON),
	}));

	const onCloseDeleteModal = () => {
		dashToDelete = -1;
		showDeleteModal = false;
	};

	const onCloseModal = () => {
		dashboardsEdit = false;
		listNewValues = items.reduce((acc, curr) => ((acc[curr.id] = curr.text), acc), {});
		editDashId = null;
		wizardStep = 0;
		dispatch('close');
	};

	const onDeleteCard = () =>
		runDelete(async () => {
			if ($location.includes('/dash/' + dashToDelete.id)) {
				replace(`/dash/${DEFAULT_DASH_ID}`);
			}
			await deleteDashboard(dashToDelete.id);
			onCloseDeleteModal();
			dashToDelete = null;
		});

	const onSave = () =>
		runSave(async () => {
			const visual = {
				icon: editDashObj.icon?.trim() || DEFAULT_DASH_ICON,
				backgroundColor: editDashObj.backgroundColor?.trim() || '',
			};
			let id;
			if (isEditDash) {
				id = editDashId;
				await requestChangeDashboardName(id, editDashObj.name, visual);
			} else {
				id = await addDashboard(editDashObj.name.trim(), visual);
				if (dashboardKind === 'camera') {
					await initializeCustomCameraDashboard(id);
				} else if (dashboardKind === 'energy') {
					await initializeCustomEnergyDashboard(id, energyPurpose);
				}
			}

			editDashObj = {
				name: '',
				icon: DEFAULT_DASH_ICON,
				backgroundColor: '',
			};
			dashboardKind = 'standard';
			energyPurpose = 'home';
			wizardStep = 0;

			const currentDash = $dashboards[id];
			// Default widgets should be created for initial default created dashboard.
			if (!currentDash?.dashWidgets && id?.toString() === DEFAULT_DASH_ID.toString()) {
				await createNewDashWithDefaultWidgets(currentDash, id);
			}

			push(`/dash/${id}`);
			onCloseModal();
		});

	const onInputName = (event: InputEvent) => {
		const target = event.target as HTMLInputElement;
		editDashObj = {
			...editDashObj,
			name: target.value,
		};
	};

	$: finalWizardStep = dashboardKind === 'energy' ? 2 : 1;
	$: isFinalWizardStep = isEditDash || wizardStep === finalWizardStep;
	$: wizardItems = [
		{
			title: $format('choose_virtual_component_type_tab', { default: 'Type' }),
			tooltipTitle: $format('dashboard_type_title', {
				default: 'Choose a dashboard type',
			}),
		},
		...(dashboardKind === 'energy'
			? [
					{
						title: $format('dashboard_wizard_preset_step', {
							default: 'Preset',
						}),
						tooltipTitle: $format('dashboard_energy_preset_title', {
							default: 'Choose an Energy preset',
						}),
					},
				]
			: []),
		{
			title: $format('wizard_items_details', { default: 'Details' }),
			tooltipTitle: $format('dashboard_wizard_details_title', {
				default: 'Dashboard details',
			}),
		},
	];
	const goBack = () => (wizardStep = Math.max(0, wizardStep - 1));
	const goNext = () => (wizardStep = Math.min(finalWizardStep, wizardStep + 1));
	const goToWizardStep = (index: number) => {
		if (index <= wizardStep) wizardStep = index;
	};
	const presetWidgetNames = (purpose: EnergyPurposeId): string[] =>
		ENERGY_V2_PURPOSE_TEMPLATES[purpose].widgets
			.filter((entry) => !entry.id.startsWith('devices-explore'))
			.map((entry) =>
				entry.type === 'dashEnergyHeatmap' && entry.energyConfig.heatmapVariant === 'minimal'
					? 'Monthly Heatmap'
					: getEnergyWidgetDefinition(entry.type).titleDefault,
			);
	const presetDescription = (purpose: EnergyPurposeId): string => {
		const preset = ENERGY_V2_PRESETS.find((entry) => entry.id === purpose);
		if (!preset) return '';

		const description = $format(preset.descriptionKey, {
			default: preset.descriptionDefault,
		});
		const bestUsage = $format(preset.bestUsageKey, {
			default: preset.bestUsageDefault,
		});
		const widgets = $format('dashboard_energy_preset_widgets', {
			default: 'Includes: {widgets}',
			values: { widgets: presetWidgetNames(purpose).join(', ') },
		});

		return `${description}<br><b>${bestUsage}</b><br>${widgets}`;
	};

	const onSelectImage = (e: CustomEvent) => {
		editDashObj = {
			...editDashObj,
			icon: e.detail.icon || editDashObj.icon || DEFAULT_DASH_ICON,
			backgroundColor: e.detail.backgroundColor ?? editDashObj.backgroundColor ?? '',
		};
	};
</script>

<!-- Ivo: New Translation for title -->
<Modal
	on:close={onCloseModal}
	showModal={dashboardsEdit}
	title={isEditDash
		? $format('modal_title_edit_dashboard', { default: 'Edit Dashboard' })
		: $format('modal_title_add_dashboard', { default: 'Add New Dashboard' })}
	contentDirection="row"
	hideSlotToolbar={isEditDash}
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, index}
			<WizardItem
				title={item.title}
				tooltipTitle={item.tooltipTitle}
				active={wizardStep === index}
				disabled={index > wizardStep}
				on:click={() => goToWizardStep(index)}
			/>
		{/each}
	</Wizard>

	<svelte:fragment slot="content">
		<!-- {#if items.length >= DASHBOARDS_LIMIT}
			<Alert
				customIcon={!isPremium ? 'fas fa-gem icon-premium-color' : ''}
				type="warning"
				heading={$format(alertMessegaLimitTranslation, {
					values: {
						limit: DASHBOARDS_LIMIT,
						premiumLimit: PREMIUM_DASH_TABS_LIMIT,
					},
					default: alertMessageReachedLimitDefault,
				})}
				class="mb-l"
				hideSlotBottom={isPremium}
			>
				<Button
					slot="bottom"
					variant="premium"
					size="m"
					width="auto"
					on:click={() => {
						onCloseModal();
						push('/settings/premium');
					}}
				>
					{capFirstChar(
						$format('button_get_premium', {
							default: 'Get Premium',
						}),
					)}
				</Button>
			</Alert>
		{:else}
			<Alert
				class="mb-l"
				heading={$format('alert_heading_dashboard_created', {
					values: { limit: DASHBOARDS_LIMIT, count: items.length },
					default: `Dashboards created: ${items.length}/${DASHBOARDS_LIMIT}`,
				})}
				message={$format('alert_message_dashboard_created', {
					values: {
						limit: DASHBOARDS_LIMIT,
						plan: plan,
					},
					default: `${plan} allows to create up to ${DASHBOARDS_LIMIT} dashboards.`,
				})}
				isHeadingBold
			/>
		{/if} -->

		{#if !isEditDash}
			<WizardContainer activeStepIndex={wizardStep} adaptiveHeight>
				<WizardContent active={wizardStep === 0}>
					<Container noScroll padding="0">
						<Text class="mb-xs text-600" element="h2">
							{$format('dashboard_type_title', {
								default: 'Choose a dashboard type',
							})}
						</Text>
						<Text class="mb-m label-extra-small-styles text-color-muted">
							{$format('dashboard_type_description', {
								default: 'Start with a focused layout or build your own from scratch.',
							})}
						</Text>
						<div class="d-flex flex-direction-column gap-s">
							{#each dashboardKindOptions as option}
								<SelectCard
									class="width-full"
									type="horizontal"
									variant="radio"
									icon={option.icon}
									title={option.label}
									descriptiveText={option.description}
									selected={dashboardKind === option.id}
									on:click={() => (dashboardKind = option.id)}
								/>
							{/each}
						</div>
					</Container>
				</WizardContent>

				{#if dashboardKind === 'energy'}
					<WizardContent active={wizardStep === 1}>
						<Container noScroll padding="0">
							<Text class="mb-xs text-600" element="h2">
								{$format('dashboard_energy_preset_title', {
									default: 'Choose an Energy preset',
								})}
							</Text>
							<Text class="mb-m text-color-muted">
								{$format('dashboard_energy_preset_description', {
									default:
										'Each preset can be customized after the dashboard is created.',
								})}
							</Text>
							<div class="d-flex flex-direction-column gap-s">
								{#each ENERGY_V2_PRESETS as preset}
									<SelectCard
										class="width-full"
										type="horizontal"
										variant="radio"
										icon={preset.icon}
										title={$format(preset.labelKey, {
											default: preset.labelDefault,
										})}
										descriptiveText={presetDescription(preset.id)}
										selected={energyPurpose === preset.id}
										on:click={() => (energyPurpose = preset.id)}
									/>
								{/each}
							</div>
						</Container>
					</WizardContent>
				{/if}

				<WizardContent active={wizardStep === finalWizardStep}>
					<Container noScroll padding="0" class="mb-m">
						<Text class="mb-s text-capitalize text-500" element="h2">
							{$format('label_menu_items_dashboard', {
								default: 'dashboard',
							})}&nbsp;{$format('name', {
								default: 'name',
							})}
						</Text>
						<Input
							class="mr-s"
							label={$format('dashboard_name_label', {
								default: 'Dashboard name',
							})}
							icon={'fas fa-objects-column'}
							value={editDashObj.name}
							id="dashboard-tab-name-input"
							on:input={onInputName}
							aria-label={$format('dashboard_name_label', {
								default: 'Dashboard name',
							})}
						/>
					</Container>
					<Container noScroll padding="0">
						<Text element="h2" class="mb-m text-500">
							{$format('select_image', { default: 'Select image' })}
						</Text>
						<ImageList
							selectedBackgroundColor={editDashObj.backgroundColor}
							selectedIcon={editDashObj.icon}
							iconsDB={icons}
							requestType="dashboard"
							showUpload={false}
							showTypeImages={false}
							showOtherImages={false}
							showIcons={true}
							showColors={true}
							coexistIconAndBackgroundColor={true}
							restrictColorsToPreviewImages={true}
							{backgroundColorPreviewImages}
							on:select-image={onSelectImage}
						/>
					</Container>
				</WizardContent>
			</WizardContainer>
		{:else}
			<Container noScroll padding="0" class="mb-m">
				<Text class="mb-s text-capitalize text-500" element="h2">
					{$format('label_menu_items_dashboard', {
						default: 'dashboard',
					})}&nbsp;{$format('name', {
						default: 'name',
					})}
				</Text>
				<Input
					class="mr-s"
					label={$format('dashboard_name_label', { default: 'Dashboard name' })}
					icon={'fas fa-objects-column'}
					value={editDashObj.name}
					id="dashboard-tab-name-input"
					on:input={onInputName}
					aria-label={$format('dashboard_name_label', {
						default: 'Dashboard name',
					})}
				/>
			</Container>
			<Container noScroll padding="0">
				<Text element="h2" class="mb-m text-500">
					{$format('select_image', { default: 'Select image' })}
				</Text>
				<ImageList
					selectedBackgroundColor={editDashObj.backgroundColor}
					selectedIcon={editDashObj.icon}
					iconsDB={icons}
					requestType="dashboard"
					showUpload={false}
					showTypeImages={false}
					showOtherImages={false}
					showIcons={true}
					showColors={true}
					coexistIconAndBackgroundColor={true}
					restrictColorsToPreviewImages={true}
					{backgroundColorPreviewImages}
					on:select-image={onSelectImage}
				/>
			</Container>
		{/if}
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		{#if !isEditDash && wizardStep > 0}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				variant="secondary"
				on:click={goBack}
			>
				{$format('button_back', { default: 'Back' })}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={onCloseModal}
		>
			{$format('button_cancel')}
		</Button>
		{#if isFinalWizardStep}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				disabled={(!isEditDash && items.length >= DASHBOARDS_LIMIT) || disableAdd}
				loading={$isSaving}
				on:click={(isEditDash || items.length <= DASHBOARDS_LIMIT) && onSave}
			>
				{$format(isEditDash ? 'button_save' : 'dashboard_create_action', {
					default: isEditDash ? 'Save' : 'Create dashboard',
				})}
			</Button>
		{:else}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				on:click={goNext}
			>
				{$format('button_next', { default: 'Next' })}
			</Button>
		{/if}
	</div>
</Modal>

<Modal
	on:close={onCloseDeleteModal}
	showModal={showDeleteModal}
	title={$format('prompt_title_delete_dashboard', {
		default: 'Delete Dashboard?',
	})}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="text-bold text-center mb-s" styleType="controls-large">
				{dashToDelete.name}
			</Text>
			<Text class="width-full">
				{$format('on_delete_item_msg', {
					values: {
						device_name: dashToDelete.name,
						type: $format('label_menu_items_dashboard', {
							default: 'Dashboard',
						}),
					},
					default: `Deleting ${$format('label_menu_items_dashboard', {
						default: 'Dashboard',
					}).toLowerCase()} ‘${dashToDelete.name}’ will permanently remove it from your account.`,
				})}
			</Text>
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseDeleteModal}
			aria-label={$format('button_cancel', { default: 'Cancel' })}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			loading={$deleteLoading}
			on:click={onDeleteCard}
			aria-label={$format('button_delete_dashboard', {
				default: 'Delete dashboard',
			})}
		>
			{$format('button_delete_dashboard', { default: 'Delete dashboard' })}
		</Button>
	</div>
</Modal>
